import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Typography, Steps, Spin, message } from 'antd';
import {
  EditOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import Step4_1_WritingPlan from './Step4_1_WritingPlan';
import Step4_2_ContentGeneration from './Step4_2_ContentGeneration';
import Step4_3_Refinement from './Step4_3_Refinement';
import Step4_4_FinalAdjustment from './Step4_4_FinalAdjustment';
import { v2ArticlesApi, publicSamplesApi } from '../../../../api';
import { useWebSocket } from '../../../../hooks/useWebSocket';
import { getUserRole } from '../../../../auth';
import TipsRotator from '../../../Common/TipsRotator';
import {
  generationStep4_1Tips,
  generationStep4_2Tips,
  generationStep4_3Tips,
  generationStep4_4Tips,
  waitingTips,
} from '../../../Common/tipsData';
import './step4.css';

const { Step } = Steps;

/**
 * STEP4: 本文生成メインコンテナ
 *
 * サブステップ:
 * 4.1 執筆計画の生成と確認
 * 4.2 本文生成（エビデンス収集 + 執筆）
 * 4.3 ブラッシュアップ（オプション）
 * 4.4 最終調整（画像挿入、エクスポート）
 */
const Step4Container = ({
  keyword,
  onComplete,
  onProgressUpdate,
  articleData: propArticleData,
  isGuest: isGuestProp,
}) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [articleData, setArticleData] = useState(propArticleData || null);
  const [userRole, setUserRole] = useState('user'); // ★追加：ユーザーロール

  // 閲覧専用モード（status='completed'の場合）
  const isReadOnly = articleData?.status === 'completed';

  // URLからサブステップを取得
  const initialSubStep = useMemo(() => {
    const path = location.pathname;
    if (path.includes('/step4-4') || path.includes('/step4.4')) return 3;
    if (path.includes('/step4-3') || path.includes('/step4.3')) return 2;
    if (path.includes('/step4-2') || path.includes('/step4.2')) return 1;
    if (path.includes('/step4-1') || path.includes('/step4.1')) return 0;
    return 0; // デフォルトは執筆プラン
  }, [location.pathname]);

  const [currentSubStep, setCurrentSubStep] = useState(initialSubStep); // 0:執筆計画, 1:本文生成, 2:推敲, 3:最終調整

  // URLが変更されたらサブステップを更新
  // ⭐ 閲覧専用モード（status='completed'）の場合はURLからの更新を許可
  useEffect(() => {
    if (articleData?.status === 'completed') {
      setCurrentSubStep(initialSubStep);
    }
  }, [initialSubStep, articleData?.status]);

  const [step4State, setStep4State] = useState({
    writingPlan: null,
    generatedContent: null,
    refinementSuggestions: null,
    finalContent: null,
  });

  // 本文生成の進捗管理（4.2用）
  const [generating, setGenerating] = useState(false);
  const [generationStarted, setGenerationStarted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentHeading, setCurrentHeading] = useState('');
  const [currentHeadingIndex, setCurrentHeadingIndex] = useState(0);
  const [totalHeadings, setTotalHeadings] = useState(0);
  const pollingIntervalRef = useRef(null);
  // WebSocket接続用（generatingとは別に管理）
  const [shouldConnectWs, setShouldConnectWs] = useState(false);

  // 生成結果
  const [generatedContents, setGeneratedContents] = useState([]);
  const [citations, setCitations] = useState([]);
  const [stats, setStats] = useState({
    total_headings: 0,
    success_count: 0,
    failed_count: 0,
    total_word_count: 0,
  });

  // プログレス状態を親コンポーネントに通知
  useEffect(() => {
    if (onProgressUpdate) {
      onProgressUpdate({
        isGenerating: generating && generationStarted,
        progress: progress,
        currentHeading: currentHeading,
      });
    }
    // onProgressUpdateを依存配列から除外（無限ループ防止）
  }, [generating, generationStarted, progress, currentHeading]);

  // WebSocket接続（STEP4.2のみ）
  // shouldConnectWs または generating が true の場合に接続
  const wsPath =
    currentSubStep === 1 && (generating || shouldConnectWs)
      ? `/ws/step4/${params.uuid}`
      : null;
  const { lastMessage, isConnected, reconnect, isOnline } = useWebSocket(wsPath);

  // generating状態が変わったらshouldConnectWsも同期
  useEffect(() => {
    if (generating) {
      setShouldConnectWs(true);
    }
  }, [generating]);

  // URLからサブステップを初期化
  // ⭐ 閲覧専用モード（status='completed'）の場合はURLからの更新を許可
  useEffect(() => {
    if (articleData?.status === 'completed') {
      setCurrentSubStep(initialSubStep);
    }
  }, [initialSubStep, articleData?.status]);

  const isGuest = isGuestProp || !localStorage.getItem('token');
  const articleApi = isGuest ? publicSamplesApi : v2ArticlesApi;

  // ★ユーザーロール取得（ゲスト時はスキップ）
  useEffect(() => {
    if (isGuest) return;
    const fetchUserRole = async () => {
      try {
        const role = await getUserRole();
        setUserRole(role);
      } catch (error) {
        console.error('Failed to get user role:', error);
      }
    };
    fetchUserRole();
  }, [isGuest]);

  // /step4へのアクセスをリダイレクト（ゲスト/完了済み→step4-4、それ以外→step4-1）
  useEffect(() => {
    if (
      params.uuid &&
      location.pathname === `/aio/generation/${params.uuid}/step4`
    ) {
      const target = isGuest ? 'step4-4' : 'step4-1';
      navigate(`/aio/generation/${params.uuid}/${target}`, { replace: true });
    }
  }, [params.uuid, location.pathname, navigate, isGuest]);

  // サブステップ変更時にURLを更新（通常モードのみ）
  useEffect(() => {
    if (!params.uuid) return;
    // ⭐ articleDataが読み込まれる前はスキップ
    if (!articleData) return;
    // ⭐ 閲覧専用モード（status='completed'）ではURL更新をスキップ
    if (isReadOnly) return;

    const subStepPaths = ['step4-1', 'step4-2', 'step4-3', 'step4-4'];
    const newPath = `/aio/generation/${params.uuid}/${subStepPaths[currentSubStep]}`;

    // 現在のパスと違う場合のみ更新
    if (location.pathname !== newPath) {
      navigate(newPath, { replace: true });
    }
  }, [
    currentSubStep,
    params.uuid,
    navigate,
    location.pathname,
    isReadOnly,
    articleData,
  ]);

  // 記事データの読み込み（初回のみ、またはURLが変わったとき）
  useEffect(() => {
    if (!params.uuid) return;

    const loadArticleData = async () => {
      try {
        setLoading(true);

        const article = await articleApi.getArticle(params.uuid);
        setArticleData(article);

        // current_stepからサブステップを決定（これが唯一の真実）
        const stepMap = {
          '4.1': 0,
          '4.2': 1,
          '4.3': 2,
          '4.4': 3,
          'step4': 0, // デフォルトは4.1
        };

        const currentStepFromDB = article.current_step || 'step4';
        const subStepFromDB =
          stepMap[currentStepFromDB] !== undefined
            ? stepMap[currentStepFromDB]
            : 0;

        // ⭐ 閲覧専用モード（status='completed'）の場合
        // URLのサブステップをそのまま使用し、リダイレクトしない
        if (article.status === 'completed') {
          // サブステップはinitialSubStep（URLから取得）を維持
          // リダイレクトしない
        } else {
          // 通常モード: サブステップをセット（URLは無視、DBの値が絶対）
          setCurrentSubStep(subStepFromDB);

          // URLとDBのステップが一致しない場合、正しいURLにリダイレクト
          const currentPath = location.pathname;
          const expectedPath = `/aio/generation/${params.uuid}/step4-${subStepFromDB + 1}`;

          if (currentPath !== expectedPath) {
            navigate(expectedPath, { replace: true });
          }
        }

        // STEP4の状態を復元（step4_statesがあれば）
        if (article.step4_states) {
          setStep4State(prev => ({
            ...prev,
            ...article.step4_states,
          }));
        }

        // 注: loadGeneratedContent()とcheckGenerationStatus()は
        // 別のuseEffectで呼ばれるため、ここでは呼ばない
      } catch (error) {
        console.error('Failed to load article:', error);
        message.error('記事データの読み込みに失敗しました');
      } finally {
        setLoading(false);
      }
    };

    loadArticleData();
  }, [params.uuid, location.pathname]); // URLが変わったときに再読み込み

  // サブステップ変更時にコンテンツをロード（STEP4.1とSTEP4.2のみ）
  useEffect(() => {
    if (!params.uuid) return;
    if (currentSubStep !== 0 && currentSubStep !== 1) return;
    if (generatedContents.length > 0) return;

    loadGeneratedContent();
  }, [currentSubStep]); // サブステップ変更時にも再実行

  // 生成状態チェック（STEP4.2のみ、初回のみ）- ゲスト時はスキップ
  useEffect(() => {
    if (!params.uuid) return;
    if (currentSubStep !== 1) return;
    if (isGuest) return;

    checkGenerationStatus();
  }, []);

  // クリーンアップ: ポーリング停止
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // WebSocketメッセージ処理
  useEffect(() => {
    if (!lastMessage) return;

    try {
      const data = JSON.parse(lastMessage);

      if (data.type === 'connected') {
        // 接続確認
      } else if (data.type === 'progress') {
        // 進捗更新
        setProgress(data.progress || 0);
        setCurrentHeading(data.current_heading || '');
        setCurrentHeadingIndex(data.current_index || 0);
        setTotalHeadings(data.total_headings || 0);

        // コンテンツをリロード（STEP4.2のみ）
        if (currentSubStep === 1) {
          loadGeneratedContent();
        }
      } else if (data.type === 'completed') {
        // 生成完了
        setGenerating(false);
        setProgress(100);

        // コンテンツをリロード（STEP4.2のみ）
        if (currentSubStep === 1) {
          loadGeneratedContent();
        }
        message.success('本文生成が完了しました');
      } else if (data.type === 'error') {
        // エラー
        console.error('[WS Step4] エラー受信:', data);
        setGenerating(false);
        message.error(data.message || '本文生成に失敗しました');
      }
    } catch (error) {
      console.error('[WS Step4] メッセージパースエラー:', error);
    }
  }, [lastMessage]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  // 本文生成のAPI処理とポーリング
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

  const loadGeneratedContent = async () => {
    // STEP4.3とSTEP4.4では不要（独自のAPIを使用）
    if (currentSubStep >= 2) {
      return;
    }

    try {
      const response = await articleApi.getGeneratedContent(params.uuid);

      setGeneratedContents(response.generated_contents || []);
      setCitations(response.citations || []);
      setStats(response.stats || {});

      // 生成が完了していれば（generating=falseのとき）、進捗を100に
      if (
        !generating &&
        response.generated_contents &&
        response.generated_contents.length > 0 &&
        response.stats?.total_word_count > 0
      ) {
        setProgress(100);
        setGenerationStarted(true);
      }
    } catch (error) {
      console.error('[Step4Container] コンテンツ読み込みエラー:', error);
    }
  };

  // ★生成状態をチェック（リロード時の自動再接続用）
  const checkGenerationStatus = async () => {
    if (!params.uuid) return;

    // STEP4.2以外では不要
    if (currentSubStep !== 1) {
      return;
    }

    try {
      const response = await v2ArticlesApi.getContentGenerationProgress(
        params.uuid,
      );

      if (response.status === 'processing' || response.status === 'reserved') {
        // WebSocket接続を先に有効化（generatingより先に設定）
        setShouldConnectWs(true);
        setGenerating(true);
        setGenerationStarted(true);

        // 進捗情報を復元
        if (response.progress !== undefined) {
          setProgress(response.progress);
        }
        if (response.current_heading) {
          setCurrentHeading(response.current_heading);
        }
        if (response.current_index !== undefined) {
          setCurrentHeadingIndex(response.current_index);
        }
        if (response.total_headings !== undefined) {
          setTotalHeadings(response.total_headings);
        }

        // ポーリング開始
        startPolling();

        message.info(
          response.status === 'reserved'
            ? '生成待機中です。順番が来たら自動で開始します。'
            : '生成中の本文を読み込みました。リアルタイム更新を再開します。',
        );
      } else if (response.status === 'completed') {
        setProgress(100);
        setShouldConnectWs(false); // WebSocket接続を無効化
      }
    } catch (error) {
      console.error(
        '[Step4Container] Failed to check generation status:',
        error,
      );
      // エラーは無視（進捗APIが存在しない場合など）
    }
  };

  const startGeneration = async () => {
    try {
      setGenerating(true);
      setGenerationStarted(true);
      setProgress(0);
      setCurrentHeading('エビデンス収集を開始しています...');
      setGeneratedContents([]);
      setCitations([]);

      await v2ArticlesApi.generateContent(params.uuid);

      // WebSocketで進捗を受信（ポーリングは不要）
      // ただし、WebSocketが接続できない場合のバックアップとしてポーリングを開始
      setTimeout(() => {
        if (progress === 0 && generating) {
          startPolling();
        }
      }, 3000); // 3秒待ってもWebSocketメッセージがなければポーリング開始
    } catch (error) {
      console.error('[Step4Container] 生成開始エラー:', error);
      message.error('本文生成の開始に失敗しました');
      setGenerating(false);
      setGenerationStarted(false);
    }
  };

  const startPolling = () => {
    let pollCount = 0;
    const maxPolls = 600; // 10分

    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;

      if (pollCount > maxPolls) {
        clearInterval(pollingIntervalRef.current);
        message.error('生成処理がタイムアウトしました');
        setGenerating(false);
        return;
      }

      try {
        // 生成結果をポーリング（進捗情報も含む）
        await loadGeneratedContent();

        const response = await v2ArticlesApi.getGeneratedContent(params.uuid);

        // 進捗情報があれば更新
        if (response.progress !== undefined) {
          setProgress(response.progress);
        } else {
          // プログレスがない場合は、完了コンテンツ数から計算
          const totalSections = articleData?.outline?.sections?.length || 1;
          const completedSections = (response.generated_contents || []).length;
          const calculatedProgress = Math.min(
            Math.round((completedSections / totalSections) * 100),
            100,
          );
          setProgress(calculatedProgress);
        }

        if (response.current_heading) {
          setCurrentHeading(response.current_heading);
        }

        if (response.current_index !== undefined) {
          setCurrentHeadingIndex(response.current_index);
        }

        if (response.total_headings !== undefined) {
          setTotalHeadings(response.total_headings);
        }

        // 生成完了チェック
        const isCompleted =
          response.status === 'completed' || response.progress >= 100;

        if (isCompleted) {
          clearInterval(pollingIntervalRef.current);
          setGenerating(false);
          setProgress(100);
          message.success('本文生成が完了しました');
        }

        // エラーチェック
        if (response.status === 'failed') {
          clearInterval(pollingIntervalRef.current);
          message.error('本文生成に失敗しました');
          setGenerating(false);
        }
      } catch (error) {
        console.error('[Step4Container] Polling error:', error);
      }
    }, 1000);
  };

  // 4.1 執筆計画が完了した時
  const handleWritingPlanCompleted = async writingPlan => {
    setStep4State(prev => ({ ...prev, writingPlan }));

    // current_stepを4.2に更新
    if (params.uuid) {
      try {
        await v2ArticlesApi.updateCurrentStep(params.uuid, '4.2');

        // articleDataを更新
        setArticleData(prev => ({ ...prev, current_step: '4.2' }));
      } catch (error) {
        console.error('[Step4Container] Failed to update current_step:', error);
        message.error('ステップの更新に失敗しました');
        return;
      }
    }

    // 本文生成中画面（4.2）に遷移
    setCurrentSubStep(1);
  };

  // 4.2 本文生成が完了した時（「推敲へ進む」ボタンクリック時）
  const handleContentGenerationCompleted = async generatedContent => {
    setStep4State(prev => ({ ...prev, generatedContent }));

    if (params.uuid) {
      try {
        // current_stepを4.3に更新
        await v2ArticlesApi.updateArticle(params.uuid, {
          current_step: '4.3',
        });

        // articleDataを更新
        setArticleData(prev => ({ ...prev, current_step: '4.3' }));

        message.success('本文生成が完了しました');
      } catch (error) {
        console.error('Failed to save generated content:', error);
        message.error('保存に失敗しました');
        return;
      }
    }

    // 推敲画面（4.3）に遷移
    setCurrentSubStep(2);
  };

  // 4.3 推敲が完了した時
  const handleRefinementCompleted = async refinedContent => {
    setStep4State(prev => ({
      ...prev,
      generatedContent: refinedContent,
      refinementSuggestions: null,
    }));

    if (params.uuid) {
      try {
        await v2ArticlesApi.updateArticle(params.uuid, {
          current_step: '4.4',
        });
        message.success('推敲が完了しました');

        // STEP4.4到達時にEXPを付与
        try {
          await v2ArticlesApi.arriveStep44(params.uuid);
        } catch (expError) {
          console.error('Failed to grant EXP on arrive step4.4:', expError);
        }
      } catch (error) {
        console.error('Failed to save refined content:', error);
      }
    }

    setCurrentSubStep(3);
  };

  // 4.4 記事完成
  const handleFinalizationCompleted = async finalData => {
    setStep4State(prev => ({ ...prev, finalContent: finalData }));

    if (params.uuid) {
      try {
        await v2ArticlesApi.updateArticle(params.uuid, {
          current_step: 'completed',
          progress: 100,
        });
        message.success('記事が完成しました！');
      } catch (error) {
        console.error('Failed to finalize article:', error);
      }
    }

    if (onComplete) {
      onComplete(finalData);
    }
  };

  // ローディング表示は各サブステップコンポーネントに任せる
  // ただし、articleDataがない場合は最小限のローディングを表示（ちらつき防止）

  if (!articleData) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '80px 20px',
          minHeight: '400px',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  // サブステップナビゲーションハンドラ
  const handleSubStepChange = targetSubStep => {
    // 閲覧専用モード（status='completed'）の場合は自由に移動可能
    if (isReadOnly) {
      const stepPathMap = {
        0: `/aio/generation/${params.uuid}/step4-1`,
        1: `/aio/generation/${params.uuid}/step4-2`,
        2: `/aio/generation/${params.uuid}/step4-3`,
        3: `/aio/generation/${params.uuid}/step4-4`,
      };
      navigate(stepPathMap[targetSubStep] || stepPathMap[0]);
      return;
    }

    // ★修正：STEP4.2（currentSubStep === 1）からの移動を完全禁止
    if (currentSubStep === 1) {
      // STEP4.2では一切のクリックを受け付けない
      return;
    }

    // ★追加：STEP4.4（currentSubStep === 3）からの逆戻りを完全禁止
    if (currentSubStep === 3) {
      // STEP4.4では一切のクリックを受け付けない
      return;
    }

    // 通常モード：進行方向のみ許可
    const stepMap = { '4.1': 0, '4.2': 1, '4.3': 2, '4.4': 3, 'step4': 0 };
    const currentStepFromDB = articleData?.current_step || 'step4';
    const dbSubStep =
      stepMap[currentStepFromDB] !== undefined ? stepMap[currentStepFromDB] : 0;

    // DBのcurrent_step以下のステップのみ移動可能
    if (targetSubStep <= dbSubStep) {
      const stepPathMap = {
        0: `/aio/generation/${params.uuid}/step4-1`,
        1: `/aio/generation/${params.uuid}/step4-2`,
        2: `/aio/generation/${params.uuid}/step4-3`,
        3: `/aio/generation/${params.uuid}/step4-4`,
      };
      navigate(stepPathMap[targetSubStep] || stepPathMap[0]);
    }
  };

  // 閲覧モード判定：URLのステップ < DBのcurrent_step の場合は閲覧のみ（通常モード）
  // または status='completed' の場合は完全な閲覧専用モード
  const stepMap = { '4.1': 0, '4.2': 1, '4.3': 2, '4.4': 3, 'step4': 0 };
  const currentStepFromDB = articleData.current_step || 'step4';
  const dbSubStep =
    stepMap[currentStepFromDB] !== undefined ? stepMap[currentStepFromDB] : 0;
  const isViewOnlyByProgress = currentSubStep < dbSubStep; // 進捗による閲覧モード

  // サブステップごとのイントロテキスト
  const getIntroText = () => {
    switch (currentSubStep) {
      case 0: // 4.1 執筆計画
        return '各見出しに対してAIが執筆方針を提案します。修正後、本文生成を開始できます。';
      case 1: // 4.2 本文生成
        return 'AIが各セクションの本文を自動生成します。エビデンス収集と執筆が並行して行われます。';
      case 2: // 4.3 推敲
        return '生成された原稿をAIが推敲・改善します。複数バージョンを比較して最適な内容を選択できます。';
      case 3: // 4.4 最終調整
        return '原稿を直接編集し、画像を挿入できます。完成したらHTML/Google Docs形式でエクスポートできます。';
      default:
        return '執筆計画を確認・編集した後、AI が各セクションの本文を自動生成します。ブラッシュアップ・画像挿入を経て記事を完成させます。';
    }
  };

  return (
    <>
      <div className="v2-form-narrow">
        {/* TIPS */}
        {(currentSubStep === 0 ||
          currentSubStep === 1 ||
          currentSubStep === 2 ||
          currentSubStep === 3) && (
          <div style={{ marginBottom: 24 }}>
            <TipsRotator
              tips={
                currentSubStep === 0
                  ? generationStep4_1Tips
                  : currentSubStep === 1
                    ? generationStep4_2Tips
                    : currentSubStep === 2
                      ? generationStep4_3Tips
                      : generationStep4_4Tips
              }
              intervalMs={7000}
              fadeMs={300}
            />
          </div>
        )}

        {/* サブステップ表示 */}
        <div style={{ marginBottom: 32 }} className="tour-step4-substeps">
          <Steps
            current={currentSubStep}
            size="small"
            className={`step4-sub-steps ${
              currentSubStep === 1 && !isReadOnly
                ? 'step4-2-navigation-locked'
                : ''
            } ${
              currentSubStep === 3 && !isReadOnly
                ? 'step4-4-navigation-locked'
                : ''
            } ${isReadOnly ? 'step4-readonly-mode' : ''}`}
            style={{
              background:
                'linear-gradient(135deg, rgba(114, 46, 209, 0.03) 0%, rgba(168, 85, 247, 0.02) 100%)',
              padding: '16px',
              borderRadius: '8px',
            }}
            onChange={handleSubStepChange}
          >
            <Step
              title="執筆プラン"
              icon={
                (isReadOnly && dbSubStep >= 0) || dbSubStep > 0 ? (
                  <CheckCircleOutlined />
                ) : (
                  <EditOutlined />
                )
              }
              description={
                <span style={{ fontSize: 12 }}>各見出しの執筆方針</span>
              }
              style={{
                cursor: isReadOnly
                  ? 'pointer'
                  : currentSubStep === 1
                    ? 'default' // ★STEP4.2では移動不可
                    : currentSubStep === 0
                      ? 'default'
                      : 'not-allowed',
              }}
            />
            <Step
              title="本文生成"
              icon={
                (isReadOnly && dbSubStep >= 1) || dbSubStep > 1 ? (
                  <CheckCircleOutlined />
                ) : (
                  <FileTextOutlined />
                )
              }
              description={
                <span style={{ fontSize: 12 }}>エビデンス収集と執筆</span>
              }
              style={{
                cursor: isReadOnly
                  ? 'pointer'
                  : currentSubStep === 1
                    ? 'default' // ★STEP4.2では移動不可
                    : currentSubStep > 1
                      ? 'default'
                      : 'not-allowed',
              }}
            />
            <Step
              title="ブラッシュアップ"
              icon={
                (isReadOnly && dbSubStep >= 2) || dbSubStep > 2 ? (
                  <CheckCircleOutlined />
                ) : (
                  <EditOutlined />
                )
              }
              description={
                <span style={{ fontSize: 12 }}>品質チェックと改善</span>
              }
              style={{
                cursor: isReadOnly
                  ? 'pointer'
                  : currentSubStep === 1
                    ? 'default' // ★STEP4.2では移動不可
                    : currentSubStep === 2
                      ? 'default'
                      : currentSubStep > 2
                        ? 'default'
                        : 'not-allowed',
              }}
            />
            <Step
              title="最終調整"
              icon={
                (isReadOnly && dbSubStep >= 3) || dbSubStep > 3 ? (
                  <CheckCircleOutlined />
                ) : (
                  <PictureOutlined />
                )
              }
              description={<span style={{ fontSize: 12 }}>画像挿入・完成</span>}
              style={{
                cursor: isReadOnly
                  ? 'pointer'
                  : currentSubStep === 1
                    ? 'default' // ★STEP4.2では移動不可
                    : currentSubStep === 3
                      ? 'default'
                      : currentSubStep > 3
                        ? 'default'
                        : 'not-allowed',
              }}
            />
          </Steps>
        </div>

        {/* コンテンツ表示 */}
        {currentSubStep === 0 && (
          <Step4_1_WritingPlan
            articleUuid={params.uuid}
            articleData={articleData}
            writingPlan={step4State.writingPlan}
            onCompleted={handleWritingPlanCompleted}
            isReadOnly={isReadOnly}
            isGuest={isGuest}
          />
        )}

        {currentSubStep === 1 && (
          <Step4_2_ContentGeneration
            articleUuid={params.uuid}
            articleData={articleData}
            writingPlan={step4State.writingPlan}
            generatedContent={step4State.generatedContent}
            onCompleted={handleContentGenerationCompleted}
            generating={generating}
            generationStarted={generationStarted}
            progress={progress}
            currentHeading={currentHeading}
            currentHeadingIndex={currentHeadingIndex}
            totalHeadings={totalHeadings}
            generatedContents={generatedContents}
            citations={citations}
            stats={stats}
            onStartGeneration={startGeneration}
            onLoadContent={loadGeneratedContent}
            userRole={userRole}
            isReadOnly={isReadOnly}
            isGuest={isGuest}
          />
        )}

        {currentSubStep === 2 && (
          <Step4_3_Refinement
            articleUuid={params.uuid}
            articleData={articleData}
            generatedContent={generatedContents}
            onCompleted={handleRefinementCompleted}
            onUpdateArticleData={setArticleData}
            isReadOnly={isReadOnly}
            isGuest={isGuest}
          />
        )}

        {currentSubStep === 3 && (
          <Step4_4_FinalAdjustment
            articleUuid={params.uuid}
            articleData={articleData}
            initialCitations={citations}
            isReadOnly={isReadOnly}
            isGuest={isGuest}
          />
        )}
      </div>
    </>
  );
};

export default Step4Container;
